<template>
  <header class="relative z-40">
    <!-- Main Navigation -->
    <nav class="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo/Brand -->
          <div class="flex-shrink-0 flex items-center">
            <NuxtLink to="/cluster" class="flex items-center space-x-2">
              <div class="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
                <i class="fas fa-network-wired text-white text-sm"></i>
              </div>
              <span class="text-xl font-bold text-gray-900">ClusterSie</span>
            </NuxtLink>
          </div>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center space-x-8">
            <NuxtLink
              to="/cluster"
              :class="[
                'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                activeSection === 'cluster' 
                  ? 'text-green-600 bg-green-50' 
                  : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
              ]"
            >
              <i class="fas fa-home mr-2"></i>หน้าหลัก
            </NuxtLink>
            
            <NuxtLink
              to="/cluster/dashboard"
              :class="[
                'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                activeSection === 'dashboard' 
                  ? 'text-green-600 bg-green-50' 
                  : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
              ]"
            >
              <i class="fas fa-tachometer-alt mr-2"></i>แดชบอร์ด
            </NuxtLink>
            
            <NuxtLink
              to="/cluster/manage"
              :class="[
                'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                activeSection === 'manage' 
                  ? 'text-green-600 bg-green-50' 
                  : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
              ]"
            >
              <i class="fas fa-cogs mr-2"></i>จัดการคลัสเตอร์
            </NuxtLink>
            
            <NuxtLink
              to="/cluster/products"
              :class="[
                'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                activeSection === 'products' 
                  ? 'text-green-600 bg-green-50' 
                  : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
              ]"
            >
              <i class="fas fa-box mr-2"></i>สินค้า
            </NuxtLink>

            <!-- User Menu -->
            <div class="flex items-center space-x-4 ml-6 pl-6 border-l border-gray-200">
              <!-- Notifications -->
              <button class="relative p-2 text-gray-600 hover:text-gray-900 transition-colors">
                <i class="fas fa-bell text-lg"></i>
                <span class="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </button>

              <!-- Profile Dropdown -->
              <div class="relative">
                <button
                  @click="toggleProfileDropdown"
                  class="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <div class="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                    <i class="fas fa-user text-gray-600 text-sm"></i>
                  </div>
                  <span class="text-sm text-gray-700">ผู้ใช้งาน</span>
                  <i class="fas fa-chevron-down text-xs text-gray-500"></i>
                </button>

                <!-- Profile Dropdown Menu -->
                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <div
                    v-show="isProfileDropdownOpen"
                    class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5"
                  >
                    <NuxtLink
                      to="/cluster/profile"
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <i class="fas fa-user-circle mr-2"></i>โปรไฟล์
                    </NuxtLink>
                    <NuxtLink
                      to="/cluster/settings"
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <i class="fas fa-cog mr-2"></i>ตั้งค่า
                    </NuxtLink>
                    <div class="border-t border-gray-100"></div>
                    <button
                      @click="handleLogout"
                      class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <i class="fas fa-sign-out-alt mr-2"></i>ออกจากระบบ
                    </button>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <!-- Mobile menu button -->
          <div class="md:hidden">
            <button
              @click="$emit('toggleMobileMenu')"
              class="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              <i class="fas fa-bars text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref } from 'vue'

// Props
const props = defineProps({
  currentSite: {
    type: String,
    default: 'cluster'
  },
  activeSection: {
    type: String,
    default: 'cluster'
  }
})

// Emits
const emit = defineEmits([
  'toggleMobileMenu',
  'openContactModal', 
  'openLoginModal',
  'smoothScrollTo'
])

// State
const isProfileDropdownOpen = ref(false)

// Methods
const toggleProfileDropdown = () => {
  isProfileDropdownOpen.value = !isProfileDropdownOpen.value
}

const handleLogout = () => {
  // Handle logout logic here
  console.log('Logout clicked')
  isProfileDropdownOpen.value = false
}

// Close dropdown when clicking outside
if (process.client) {
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.relative')) {
      isProfileDropdownOpen.value = false
    }
  })
}
</script>

<style scoped>
/* Navigation active state styles */
.router-link-active {
  color: #059669;
  background-color: #ecfdf5;
}

/* Mobile menu animation */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.3s ease-in-out;
}

.mobile-menu-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .mobile-menu-panel {
    max-height: 70vh;
    overflow-y: auto;
  }
}
</style>